import React, { useEffect, useMemo, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarExport,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import { Button, FormControl, TextField } from '@mui/material';
import ConfirmDeleteDialog from './Modal/ConfirmDelete';
import UserApiService from '../api/UserApiService';
import UsersEditMenuButton from './Admin/Users/UsersEditMenuButton';
import ModifyUserModal from './Modal/Admin/ModifyUserModal';
import OrgApiService from '../api/OrgApiService';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AuthUtil from './Utils/AuthUtil';
import authService from '../auth/AuthService';
import { useSnackbar } from './Common/SnackbarProvider';

const userApiService = new UserApiService();
const orgApiService = new OrgApiService();

const Users = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedUser, setCurrentSelectedUser] = useState();
    const [openAddEditUserModal, setOpenAddEditUserModal] = useState(false);
    const [possibleOrgs, setPossibleOrgs] = useState([]);
    const [quickSearchText, setQuickSearchText] = useState('');
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [userToImpersonate, setUserToImpersonate] = useState(null);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);
    const currentUserId = useMemo(() => Number(AuthUtil.getUserId(userToken)), [userToken]);

    /**
     * Impersonation is a super-admin-only tool, and the server additionally refuses inactive
     * users, other super admins and self-impersonation. Mirror those rules here so the action
     * only appears where it will actually work.
     * @param user {object} row from the users grid
     */
    const canImpersonateUser = (user) =>
        isSuperAdmin
        && Boolean(user.mainSettingsDto.usActive)
        && !user.mainSettingsDto.isSuperAdmin
        && user.usId !== currentUserId;

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        );
    }

    const columns = useMemo(() => [
        {
            field: 'usId',
            headerName: t('common.id'),
            flex: 0.5,
        },
        {
            field: 'mainSettingsDto.usUsername',
            headerName: t('userMainSettings.field.username'),
            flex: 2,
            valueGetter: (params) => {
                return params.row.mainSettingsDto.usUsername;
            },
        },
        {
            field: 'mainSettingsDto.usFirstname',
            headerName: t('userMainSettings.field.firstName'),
            flex: 2,
            valueGetter: (params) => {
                return params.row.mainSettingsDto.usFirstname;
            },
        },
        {
            field: 'mainSettingsDto.usLastname',
            headerName: t('userMainSettings.field.lastName'),
            flex: 2,
            valueGetter: (params) => {
                return params.row.mainSettingsDto.usLastname;
            },
        },
        {
            field: 'mainSettingsDto.usEmail',
            headerName: t('fieldName.email'),
            flex: 2,
            valueGetter: (params) => {
                return params.row.mainSettingsDto.usEmail;
            },
        },
        {
            field: 'mainSettingsDto.usActive',
            headerName: t('common.active'),
            flex: 1,
            valueGetter: (params) => {
                return params.row.mainSettingsDto.usActive;
            },
            renderCell: (params) => {
                if (params.row.mainSettingsDto.usActive > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'userAdminMainSettings.usOrg',
            headerName: t('fieldName.organization'),
            flex: 1,
            valueGetter: (params) => {
                return params.row.userOrg?.ogName ?? '';
            },
            renderCell: (params) => {
                return params.row.userOrg?.ogName ?? '';
            }
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <UsersEditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedUser={setCurrentSelectedUser}
                    setShowEditDialog={setOpenAddEditUserModal}
                    isActive={Boolean(params.row.mainSettingsDto.usActive)}
                    canImpersonate={canImpersonateUser(params.row)}
                    onImpersonate={setUserToImpersonate}
                />;
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, isSuperAdmin, currentUserId]);

    const onDeleteClick = async (value) => {
        if (!value) {
            setShowDeleteDialog(false);
            return;
        }
        if (currentSelectedUser?.mainSettingsDto.usActive) {
            const deactivate = await userApiService.deactivateUser(currentSelectedUser.usId);
            if (deactivate && deactivate.success) {
                snackbar.warning(t('usersPage.toast.deactivated'));
                updateUserActive(currentSelectedUser.usId, false);
            }
        } else {
            const activate = await userApiService.activateUser(currentSelectedUser.usId);
            if (activate && activate.success) {
                snackbar.success(t('usersPage.toast.activated'));
                updateUserActive(currentSelectedUser?.usId, true);
            }
        }
        setShowDeleteDialog(false);
    };

    /**
     * Confirms and starts an impersonation session, then reloads onto the home page so nothing
     * fetched as the super admin lingers in the impersonated session.
     * @param confirmed {boolean} result of the confirmation dialog
     */
    const onImpersonateConfirm = async (confirmed) => {
        if (!confirmed || !userToImpersonate) {
            setUserToImpersonate(null);
            return;
        }

        const response = await authService.impersonate(userToImpersonate.usId);
        if (response && response.success) {
            window.location.href = '/';
            return;
        }

        snackbar.error(response?.message ?? t('usersPage.toast.impersonateError'));
        setUserToImpersonate(null);
    };

    /**
     * Updates user in state
     * @param userId {number}
     * @param wasActivated {boolean}
     */
    function updateUserActive (userId, wasActivated) {
        const usersCopy = JSON.parse(JSON.stringify(users));
        usersCopy.find(x => {
            return x.usId === userId;
        }).mainSettingsDto.usActive = wasActivated ? 1 : 0;
        setUsers(usersCopy);
        resetSearchedUsers(usersCopy);
    }

    async function populateUsers () {
        const response = await userApiService.getUsers();
        if (response != null && response.success) {
            setUsers(response.data);
            setSearchedUsers(response.data);
        }
    }

    async function getPossibleOrgs () {
        const response = await orgApiService.getSelectableOrgs();
        if (response && response.success) {
            setPossibleOrgs(response.data);
        } else {
            snackbar.error(t('usersPage.toast.orgsError'));
        }
    }

    function handleSearchChange(event) {
        const searchText = event.target.value;
        setQuickSearchText(searchText);
        setSearchedUsers(users.filter((user) => user.mainSettingsDto.usUsername.includes(searchText) || (parseInt(searchText) > 0 && user.usId === parseInt(searchText)) || user.mainSettingsDto.usFirstname.includes(searchText) || user.mainSettingsDto.usLastname.includes(searchText) || user.mainSettingsDto.usEmail.includes(searchText)));
    }

    function resetSearchedUsers(updatedUsers) {
        setSearchedUsers(updatedUsers.filter((user) => user.mainSettingsDto.usUsername.includes(quickSearchText) || (parseInt(quickSearchText) > 0 && user.usId === parseInt(quickSearchText)) || user.mainSettingsDto.usFirstname.includes(quickSearchText) || user.mainSettingsDto.usLastname.includes(quickSearchText) || user.mainSettingsDto.usEmail.includes(quickSearchText)));
    }

    useEffect(() => {
        async function setupData () {
            setLoading(true);
            await populateUsers();
            await getPossibleOrgs();
            setLoading(false);
        }

        setupData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Box sx={{ mb: 2 }}>
                <h2>{t('usersPage.title')}</h2>
            </Box>
            <Box sx={{ mb: 2 }}>
                <div style={{ display: 'inline-block', justifyContent: 'space-between' }}>
                    <FormControl sx={{ m: 1, minWidth: 120, float: 'left' }}>
                        <Button size={'large'} variant="contained" onClick={() => setOpenAddEditUserModal(true) }>{t('usersPage.createButton')}</Button>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 200, float: 'right'}}>
                        <TextField
                            value={quickSearchText}
                            onChange={handleSearchChange}
                            label={t('usersPage.quickSearch')}
                        />
                    </FormControl>
                </div>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    rows={searchedUsers}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.usId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    getRowHeight={() => 'auto'}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                />
            </Box>

            <ConfirmDeleteDialog
                id="confirm-delete"
                keepMounted
                value={currentSelectedUser}
                open={showDeleteDialog}
                title={currentSelectedUser?.usActive ? t('usersPage.confirmTitleDeactivate') : t('usersPage.confirmTitleActivate')}
                content={currentSelectedUser?.usActive ? t('usersPage.confirmContentDeactivate') : t('usersPage.confirmContentActivate')}
                onClose={onDeleteClick}
            />
            <ConfirmDeleteDialog
                id="confirm-impersonate"
                keepMounted
                open={Boolean(userToImpersonate)}
                title={t('usersPage.confirmTitleImpersonate')}
                content={t('usersPage.confirmContentImpersonate', {
                    user: userToImpersonate?.mainSettingsDto.usUsername ?? '',
                })}
                onClose={onImpersonateConfirm}
            />
            <ModifyUserModal
                open={openAddEditUserModal}
                orgs={possibleOrgs}
                selectedUser={currentSelectedUser}
                onClose={async (response) => {
                    if (response) {
                        setOpenAddEditUserModal(false);
                        setCurrentSelectedUser(null);
                        await populateUsers();
                    }
                }}
                onCancel={() => {
                    setOpenAddEditUserModal(false);
                    setCurrentSelectedUser(null);
                }}
                userApiService={userApiService}
                setCurrentSelectedUser={setCurrentSelectedUser}
            />
        </Box>
    );
};

export default Users;
